import { BRAILLER_LOAN_STATUS, REPAIR_STATUS, BRAILLER_REPLACEMENT_STATUS_LIST } from '../braillerConstants';

export const getChangedLoanManagementItemsForUpdate = (currentItems: Record<string, any>[], originalItems: Record<string, any>[]) => {
const changedItems: Record<string, any>[] = [];

currentItems.forEach((currentItem: Record<string, any>) => {
    const originalItem = originalItems.find(
        (item: Record<string, any>) => item.id === currentItem.id && item.type === currentItem.type
    );

        if (!originalItem) return;

        const hasStatusChanged = currentItem.type === 'Loan'
            ? currentItem.loanStatus !== originalItem.loanStatus
            : currentItem.repairStatus !== originalItem.repairStatus;

        const hasNotesChanged = currentItem.notes !== originalItem.notes;

        if (hasStatusChanged || hasNotesChanged) {
            if (currentItem.type === 'Loan') {
                changedItems.push({
                    braillerLoanId: currentItem.braillerLoanId,
                    braillerLoanStatus: currentItem.loanStatus,
                    braillerLoanNotes: currentItem.notes,
                });
            } else {
                // Repair item
                changedItems.push({
                    type: 'Repair',
                    id: currentItem.braillerRepairId,
                    resolution: currentItem.notes,
                    repairedBy: currentItem.repairedBy || 'System',
                    status: currentItem.repairStatus,
                    repairCost: currentItem.repairCost,
                });
            }
        }
    });

    return changedItems;
};

/**
 * Copy text to clipboard with fallback for older browsers
 * @param {string} textToCopy - The text to copy to clipboard
 */
export async function copyToClipboard(textToCopy: string) {
    if (!navigator.clipboard) {
        alert("Copy not supported. Try again.");
        return;
    }
    try {
        await navigator.clipboard.writeText(textToCopy);
        setTimeout(() => alert("Address copied to clipboard."), 1000);
    } catch (err) {
        alert("Copy failed. Try again.");
    }
}

/**
 * Get display label for Brailler Loan Status
 * Uses centralized enum constants
 */
export const getLoanStatusDisplay = (status: string | number | null) => {
const statusMap: Record<string | number, string> = {
        [BRAILLER_LOAN_STATUS.ReadyForCheckout.id]: BRAILLER_LOAN_STATUS.ReadyForCheckout.label,
        [BRAILLER_LOAN_STATUS.WaitList.id]: BRAILLER_LOAN_STATUS.WaitList.label,
        [BRAILLER_LOAN_STATUS.CheckedOut.id]: BRAILLER_LOAN_STATUS.CheckedOut.label,
        [BRAILLER_LOAN_STATUS.Returned.id]: BRAILLER_LOAN_STATUS.Returned.label,
        [BRAILLER_LOAN_STATUS.Shipped.id]: BRAILLER_LOAN_STATUS.Shipped.label,
        [BRAILLER_LOAN_STATUS.RepairRequested.id]: BRAILLER_LOAN_STATUS.RepairRequested.label,
        [BRAILLER_LOAN_STATUS.RepairShipped.id]: BRAILLER_LOAN_STATUS.RepairShipped.label,
        [BRAILLER_LOAN_STATUS.Cancelled.id]: BRAILLER_LOAN_STATUS.Cancelled.label,
    };
    return status != null ? statusMap[status] || 'Unknown' : 'Unknown';
};

export const getRepairStatusDisplay = (status: string | number | null) => {
const statusMap: Record<string | number, string> = {
        [REPAIR_STATUS.Reported.id]: REPAIR_STATUS.Reported.label,
        [REPAIR_STATUS.Approved.id]: REPAIR_STATUS.Approved.label,
        [REPAIR_STATUS.Replaced.id]: REPAIR_STATUS.Replaced.label,
        [REPAIR_STATUS.Repaired.id]: REPAIR_STATUS.Repaired.label,
        [REPAIR_STATUS.Denied.id]: REPAIR_STATUS.Denied.label,
    };
    return status != null ? statusMap[status] || 'Unknown' : 'Unknown';
};

/**
 * Get display label for Brailler Replacement Status
 * @param {number} statusId - The enum value (1-4)
 * @returns {string} The display label or "Unknown"
 */
export const getReplacementStatusLabel = (statusId: string | number | null) => {
const status = BRAILLER_REPLACEMENT_STATUS_LIST.find((s: Record<string, any>) => s.value === statusId);
    return status ? status.text : "Unknown";
};

/**
 * Checks if the repair status is active (not terminal)
 * Active statuses: Reported, Approved
 */
export const isActiveRepairStatus = (status: string | number) => {
    return status === REPAIR_STATUS.Reported.id || status === REPAIR_STATUS.Approved.id;
};

/**
 * Checks if the repair status is terminal (closed)
 * Terminal statuses: Repaired, Denied, Replaced
 */
export const isTerminalRepairStatus = (status: string | number) => {
    return status === REPAIR_STATUS.Repaired.id || 
           status === REPAIR_STATUS.Denied.id || 
           status === REPAIR_STATUS.Replaced.id;
};

/**
 * Checks if a loan status is terminal (closed)
 * Terminal statuses: Returned, Cancelled
 */
export const isTerminalLoanStatus = (status: string | number) => {
    return status === BRAILLER_LOAN_STATUS.Returned.id || 
           status === BRAILLER_LOAN_STATUS.Cancelled.id;
};

/**
 * Checks if a loan can be cancelled
 * A loan can be cancelled if it's not already returned or cancelled
 */
export const canCancelLoan = (status: string | number | null) => {
    return status !== BRAILLER_LOAN_STATUS.Returned.id && 
           status !== BRAILLER_LOAN_STATUS.Cancelled.id;
};

/**
 * Gets a warning message for cancelling based on loan status
 * @param {number} status - The current loan status
 * @returns {string} Warning message or empty string
 */
export const getCancelWarningMessage = (status: string | number | null) => {
    switch (status) {
        case BRAILLER_LOAN_STATUS.Shipped.id:
            return 'This brailler has been shipped to the patron.';
        case BRAILLER_LOAN_STATUS.CheckedOut.id:
            return 'This brailler is currently checked out.';
        case BRAILLER_LOAN_STATUS.RepairRequested.id:
            return 'This brailler has a repair request.';
        case BRAILLER_LOAN_STATUS.RepairShipped.id:
            return 'This brailler has been shipped for repair.';
        default:
            return '';
    }
};

export const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

export const getItemTypeDisplay = (type: string) => {
    return type === 'Loan' ? '?? Loan' : '?? Repair';
};

export const isRepairItem = (item: Record<string, any>) => item.type === 'Repair';
export const isLoanItem = (item: Record<string, any>) => item.type === 'Loan';
export const isUnloanedRepair = (item: Record<string, any>) => item.type === 'Repair' && !item.braillerLoanId;
